<script setup lang="ts">
import {
  estateObjectLoanCalculations,
  type EstateObjectLoanCalculations,
} from '@/components/estate-objects/estate-object-calculations-utils'
import { usePdfExport } from '@/composables/usePdfExport'
import { useFeatureFlagStore } from '@/stores/features'
import EstateObjectsController from '@controllers/Investors/EstateObjectsController'
import { router } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import LoanData = App.Data.EstateObject.LoanData

const { t, n } = useI18n()

type EstateObjectOverviewRow = EstateObjectData & EstateObjectLoanCalculations

const props = withDefaults(
  defineProps<{
    estateObjects: EstateObjectData[]
    print?: boolean
    filter?: string
    showBankLoanMetrics?: boolean
  }>(),
  {
    showBankLoanMetrics: false,
  },
)

const estateObjectRows = computed<EstateObjectOverviewRow[]>(() =>
  props.estateObjects.map((estateObject) => ({
    ...estateObject,
    ...estateObjectLoanCalculations(estateObject.loans),
  })),
)

const isPortfolio = computed(() => {
  return props.filter === 'portfolio'
})

const totalSizeLiving = computed(() => {
  return props.estateObjects.reduce((sum, obj) => sum + (obj.living_space_size || 0), 0)
})

const totalSizeCommercial = computed(() => {
  return props.estateObjects.reduce((sum, obj) => sum + (obj.business_space_size || 0), 0)
})

const totalPurchasePrice = computed(() => {
  return props.estateObjects.reduce((sum, obj) => sum + (obj.purchase_price || 0), 0)
})

const totalNumberOfUnits = computed(() => {
  return props.estateObjects.reduce((sum, obj) => sum + (obj.number_of_units || 0), 0)
})

const totalRent = computed(() => {
  return props.estateObjects.reduce((sum, obj) => sum + (obj.actual_rent * 12 || 0), 0)
})

const totalMortgage = computed(() => {
  return props.estateObjects.reduce((sum, estate) => {
    const estateTotal = estate.loans?.reduce((loanSum, loan) => loanSum + (loan.mortgage || 0), 0)
    return sum + (estateTotal || 0)
  }, 0)
})

const totalAnnuity = computed(() => {
  return props.estateObjects.reduce((sum, estate) => {
    const estateTotal = estate.loans?.reduce((loanSum, loan) => loanSum + (loan.installment_amount || 0), 0)
    return sum + (estateTotal || 0)
  }, 0)
})

const totalBankLoanAmount = computed(() =>
  estateObjectRows.value.reduce((sum, estateObject) => sum + (estateObject.totalLoanAmount || 0), 0),
)

const totalBankMonthlyAnnuity = computed(() =>
  estateObjectRows.value.reduce((sum, estateObject) => sum + (estateObject.totalMonthlyAnnuity || 0), 0),
)

const totalBankLoanCount = computed(() =>
  estateObjectRows.value.reduce((sum, estateObject) => sum + estateObject.loanCount, 0),
)

const sumLoanField = (loans: LoanData[] | undefined, field: 'mortgage' | 'installment_amount') => {
  return (
    loans?.reduce((sum: number, loan: LoanData) => {
      return sum + ((loan[field] as number | null | undefined) || 0)
    }, 0) ?? 0
  )
}

const confirm = useConfirm()

const features = useFeatureFlagStore()

const { generatePdf, previewPdf, generatingPdf } = usePdfExport()

const deleteEstateObject = (id: string) => {
  confirm.require({
    header: t('confirm.header'),
    message: t('confirm.delete'),
    rejectProps: {
      label: t('confirm.reject'),
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: t('confirm.accept'),
      severity: 'danger',
    },
    accept: () => {
      router.delete(EstateObjectsController.destroy(id).url, { preserveScroll: true })
    },
  })
}
</script>

<template>
  <DataTable v-if="estateObjectRows.length" :value="estateObjectRows" :scrollable="!print">
    <Column :header="t('table.index')">
      <template #body="{ index }">
        {{ index + 1 }}
      </template>
    </Column>
    <Column field="title" :header="t('table.title')" frozen>
      <template #body="{ data }">
        <div class="flex items-center gap-3">
          <TwicImg
            v-if="!print && data.primary_photo?.path"
            :src="data.primary_photo.path"
            class="size-10 rounded object-cover" />
          <div
            v-else-if="!print"
            class="bg-surface-200 text-surface-500 flex size-10 items-center justify-center rounded">
            <i class="pi pi-image" />
          </div>
          <Link :href="EstateObjectsController.show(data.id)" class="font-medium text-nowrap">
            {{ data.title }}
          </Link>
        </div>
      </template>
    </Column>
    <Column :header="t('table.type')">
      <template #body="{ data }">
        <div v-if="data.estate_object_type">
          {{ t(`enums.estateObjectType.${data.estate_object_type}`) }}
        </div>
        <div v-else>---</div>
      </template>
    </Column>
    <Column v-if="!print" field="address" :header="t('table.address')" />
    <Column :header="t('table.owner')">
      <template #body="{ data }">
        <div class="text-nowrap">
          {{ data.owner?.name || '---' }}
        </div>
      </template>
    </Column>
    <Column field="size" :header="t('table.size')">
      <template #body="{ data }">
        <div class="flex flex-col items-end">
          <div v-if="data.living_space_size">{{ n(data.living_space_size) }} {{ t('units.square_meter') }}</div>
          <div v-else>&mdash;</div>
          <div v-if="data.business_space_size">{{ n(data.business_space_size) }} {{ t('units.square_meter') }}</div>
          <div v-else>&mdash;</div>
        </div>
      </template>
      <template #footer>
        <div class="flex justify-between gap-4">
          <span>{{ t('footer.livingSpace') }}</span>
          <span class="font-semibold text-nowrap"> {{ n(totalSizeLiving) }} {{ t('units.square_meter') }} </span>
        </div>
        <div class="flex justify-between gap-4">
          <span>{{ t('footer.commercialSpace') }}</span>
          <span class="font-semibold text-nowrap"> {{ n(totalSizeCommercial) }} {{ t('units.square_meter') }} </span>
        </div>
      </template>
    </Column>
    <Column field="number_of_units" :header="t('table.number_of_units')">
      <template #body="{ data }">
        <div class="text-right">
          {{ n(data.number_of_units || 0) }}
        </div>
      </template>
      <template #footer>
        <div class="text-right font-semibold text-nowrap">
          {{ n(totalNumberOfUnits || 0) }}
        </div>
      </template>
    </Column>
    <Column field="construction_year" :header="t('table.construction_year')">
      <template #body="{ data }">
        <div class="text-center text-nowrap">
          {{ data.construction_year }}
        </div>
      </template>
    </Column>
    <Column :header="t('table.purchase_price')">
      <template #body="{ data }">
        <div class="text-right">
          {{ n(data.purchase_price || 0, 'euros') }}
        </div>
      </template>
      <template #footer>
        <div class="text-right font-semibold">
          {{ n(totalPurchasePrice, 'euros') }}
        </div>
      </template>
    </Column>
    <Column v-if="showBankLoanMetrics" field="totalLoanAmount" :header="t('table.loan_bank')">
      <template #body="{ data }">
        <div class="text-right">
          {{ n(data.totalLoanAmount || 0, 'euros') }}
        </div>
      </template>
      <template #footer>
        <div class="text-right font-semibold">
          {{ n(totalBankLoanAmount, 'euros') }}
        </div>
      </template>
    </Column>
    <Column v-if="showBankLoanMetrics" field="totalMonthlyAnnuity" :header="t('table.annuity_bank')">
      <template #body="{ data }">
        <div class="text-right">
          {{ n(data.totalMonthlyAnnuity || 0, 'euros') }}
        </div>
      </template>
      <template #footer>
        <div class="text-right font-semibold">
          {{ n(totalBankMonthlyAnnuity, 'euros') }}
        </div>
      </template>
    </Column>
    <Column v-if="showBankLoanMetrics" field="loanCount" :header="t('table.loan_count')">
      <template #body="{ data }">
        <div class="text-right">
          {{ n(data.loanCount || 0) }}
        </div>
      </template>
      <template #footer>
        <div class="text-right font-semibold">
          {{ n(totalBankLoanCount || 0) }}
        </div>
      </template>
    </Column>
    <Column v-if="isPortfolio" :header="t('table.current_balance')">
      <template #body="{ data }">
        <div v-if="data.loans_sum_current_balance" class="text-right">
          {{ n(data.loans_sum_current_balance / 100, 'euros') }}
        </div>
        <div v-else class="text-right">&mdash;</div>
      </template>
      <template #footer>
        <div class="text-right font-semibold">
          {{
            n(
              estateObjects.reduce(
                (sum, obj) => sum + (obj.loans_sum_current_balance ? obj.loans_sum_current_balance / 100 : 0),
                0,
              ),
              'euros',
            )
          }}
        </div>
      </template>
    </Column>
    <Column :header="t('table.actual_rent')">
      <template #body="{ data }">
        <div class="text-right">
          {{ n(data.actual_rent * 12, 'euros') }}
        </div>
      </template>
      <template #footer>
        <div class="text-right font-semibold">
          {{ n(totalRent, 'euros') }}
        </div>
      </template>
    </Column>
    <Column v-if="isPortfolio" :header="t('table.mortgage')">
      <template #body="{ data }">
        <div class="text-right">
          <div v-if="data.loans && data.loans.length > 0">
            {{ n(sumLoanField(data.loans, 'mortgage'), 'euros') }}
          </div>
          <div v-else>&mdash;</div>
        </div>
      </template>
      <template #footer>
        <div class="text-right font-semibold">
          {{ n(totalMortgage, 'euros') }}
        </div>
      </template>
    </Column>
    <Column v-if="isPortfolio" :header="t('table.installment_amount')">
      <template #body="{ data }">
        <div class="text-right">
          <div v-if="data.loans && data.loans.length > 0">
            {{ n(sumLoanField(data.loans, 'installment_amount'), 'euros') }}
          </div>
          <div v-else>&mdash;</div>
        </div>
      </template>
      <template #footer>
        <div class="text-right font-semibold">
          {{ n(totalAnnuity, 'euros') }}
        </div>
      </template>
    </Column>
    <Column :header="t('table.rental_yield')">
      <template #body="{ data }">
        <div v-if="data.purchase_price" class="text-right">
          {{ n((data.actual_rent * 12) / data.purchase_price, 'percent') }}
        </div>
      </template>
      <template #footer>
        <div v-if="totalPurchasePrice" class="text-right font-semibold">
          {{ n(totalRent / totalPurchasePrice, 'percent') }}
        </div>
      </template>
    </Column>
    <Column v-if="!print" frozen align-frozen="right" class="action-column">
      <template #body="{ data }">
        <div class="flex gap-2">
          <Button
            v-if="features.enabled('previewPdf')"
            severity="info"
            variant="outlined"
            icon="pi pi-expand"
            size="small"
            @click="previewPdf({ template: 'single-object', id: data.id })" />
          <Button
            severity="primary"
            icon="pi pi-file-pdf"
            size="small"
            :loading="generatingPdf"
            @click="generatePdf({ template: 'single-object', id: data.id })" />
          <Link :href="EstateObjectsController.show(data.id)">
            <Button severity="secondary" icon="pi pi-pencil" size="small" />
          </Link>
          <Button severity="secondary" icon="pi pi-trash" size="small" @click="deleteEstateObject(data.id)" />
        </div>
      </template>
    </Column>
  </DataTable>
</template>

<style>
.action-column {
  box-shadow: -5px 0 30px -5px rgba(0, 0, 0, 0.1);
}
</style>

<i18n>
{
  "de": {
    "table": {
      "index": "Nr.",
      "title": "Name",
      "type": "Typ",
      "address": "Adresse",
      "owner": "Eigentümer",
      "size": "Wohnfläche / Gewerbefläche",
      "number_of_units": "Einheiten",
      "construction_year": "Baujahr",
      "current_balance": "Restschuld",
      "purchase_price": "Kaufpreis",
      "loan_bank": "Kredit-Bank",
      "annuity_bank": "Annuität Bank",
      "loan_count": "Anzahl Darlehen",
      "actual_rent": "Nettomiete p.a.",
      "mortgage": "Grundschuld",
      "installment_amount": "Annuität p.m.",
      "rental_yield": "Mietrendite"
    },
    "footer": {
      "livingSpace": "Wohnen",
      "commercialSpace": "Gewerbe"
    },
    "units": {
      "square_meter": "m²"
    },
    "export": {
      "summary": "PDF wird generiert",
      "detail": "Der Download startet automatisch, sobald der Export generiert wurde.",
      "error": {
        "summary": "Fehler beim Exportieren",
        "detail": "Es gab ein Problem beim Generieren des PDF. Bitte versuche es später erneut."
      },
      "preview_error": {
        "summary": "Fehler bei der Vorschau",
        "detail": "Es gab ein Problem beim Erstellen der PDF-Vorschau. Bitte versuche es später erneut."
      }
    },
    "confirm": {
      "header": "Bestätigung",
      "delete": "Möchtest du diese Immobilie wirklich löschen?",
      "reject": "Abbrechen",
      "accept": "Löschen"
    }
  },
  "en": {
    "table": {
      "index": "No.",
      "title": "Name",
      "type": "Type",
      "address": "Address",
      "owner": "Owner",
      "size": "Living / Commercial Area",
      "number_of_units": "Units",
      "construction_year": "Year of Construction",
      "current_balance": "Remaining Debt",
      "purchase_price": "Purchase Price",
      "loan_bank": "Loan (Bank)",
      "annuity_bank": "Annuity (Bank)",
      "loan_count": "Number of Loans",
      "mortgage": "Mortgage",
      "installment_amount": "Annuity p.m.",
      "actual_rent": "Net Rent p.a.",
      "rental_yield": "Rental Yield"
    },
    "footer": {
      "livingSpace": "Living",
      "commercialSpace": "Commercial"
    },
    "units": {
      "square_meter": "m²"
    },
    "export": {
      "summary": "PDF is being generated",
      "detail": "The download will start automatically once the export is generated.",
      "error": {
        "summary": "Error during export",
        "detail": "There was a problem generating the PDF. Please try again later."
      },
      "preview_error": {
        "summary": "Error during preview",
        "detail": "There was a problem generating the PDF preview. Please try again later."
      }
    },
    "confirm": {
      "header": "Confirmation",
      "delete": "Are you sure you want to delete this property?",
      "reject": "Cancel",
      "accept": "Delete"
    }
  }
}
</i18n>
